import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Select, Button, Checkbox, Alert, Divider, Tag } from "antd";
import { CheckOutlined, ReloadOutlined } from "@ant-design/icons";

const { Option } = Select;

// ─── Helper: fetch all pages ──────────────────────────────────────────────────
async function fetchAllPages(initialUrl, headers, backUrl) {
    let results = [];
    let url = initialUrl;
    while (url) {
        const res = await axios.get(url, { headers });
        const data = res.data;
        if (Array.isArray(data)) {
            results = [...results, ...data];
            break;
        }
        results = [...results, ...(data.results || [])];
        if (data.next) {
            url = data.next.startsWith("http") ? data.next : `${backUrl}${data.next}`;
        } else {
            url = null;
        }
    }
    return results;
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ChangeHod() {
    const { authData, backUrl } = useContext(AuthContext);

    const [hods, setHods] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [reportingEmployees, setReportingEmployees] = useState([]);
    const [outgoingId, setOutgoingId] = useState(null);
    const [incomingId, setIncomingId] = useState(null);
    const [selectedEmpIds, setSelectedEmpIds] = useState([]);
    const [loadingHods, setLoadingHods] = useState(true);
    const [loadingEmps, setLoadingEmps] = useState(true);
    const [loadingReporting, setLoadingReporting] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState("");
    const [error, setError] = useState("");

    const headers = { Authorization: `Bearer ${authData?.access}` };

    const fetchHods = async () => {
        setLoadingHods(true);
        try {
            const results = await fetchAllPages(`${backUrl}/api/emp/employees/?is_hod=1`, headers, backUrl);
            setHods(results);
        } catch {
            setError("Failed to load HOD list.");
        } finally {
            setLoadingHods(false);
        }
    };

    const fetchAllEmployees = async () => {
        setLoadingEmps(true);
        try {
            const results = await fetchAllPages(`${backUrl}/api/emp/employees/`, headers, backUrl);
            setAllEmployees(results);
        } catch {
            setError("Failed to load employee list.");
        } finally {
            setLoadingEmps(false);
        }
    };

    const fetchReportingEmployees = async (hodId) => {
        setLoadingReporting(true);
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        try {
            const results = await fetchAllPages(`${backUrl}/api/emp/employees/?reporting_to=${hodId}`, headers, backUrl);
            setReportingEmployees(results);
        } catch {
            setError("Failed to load reporting employees.");
        } finally {
            setLoadingReporting(false);
        }
    };

    useEffect(() => {
        fetchHods();
        fetchAllEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOutgoingChange = (v) => {
        setOutgoingId(v);
        setIncomingId(null);
        setSuccess("");
        setError("");
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        if (v) fetchReportingEmployees(v);
    };

    const handleToggleEmp = (id) => {
        setSelectedEmpIds((prev) =>
            prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
        );
    };

    const handleToggleAll = (e) => {
        setSelectedEmpIds(e.target.checked ? reportingEmployees.map((emp) => emp.id) : []);
    };

    const reset = () => {
        setOutgoingId(null);
        setIncomingId(null);
        setReportingEmployees([]);
        setSelectedEmpIds([]);
        setSuccess("");
        setError("");
    };

    const replaceHod = async () => {
        setSubmitting(true);
        setSuccess("");
        setError("");
        try {
            await axios.post(
                `${backUrl}/api/emp/hod-replacement/`,
                {
                    outgoing_hod_id: Number(outgoingId),
                    incoming_hod_id: Number(incomingId),
                    employee_ids: selectedEmpIds,
                },
                { headers }
            );
            setSuccess("HOD replaced successfully!");
            reset();
            fetchHods();
            fetchAllEmployees();
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                err?.response?.data?.message ||
                "Replacement failed. Please try again."
            );
        } finally {
            setSubmitting(false);
        }
    };

    const getLabel = (emp) =>
        [emp.salutation, emp.first_name, emp.last_name].filter(Boolean).join(" ") || emp.emp_code;

    const outgoingEmp = hods.find((h) => h.id === outgoingId);
    const incomingEmp = allEmployees.find((e) => e.id === incomingId);
    const allSelected = reportingEmployees.length > 0 && reportingEmployees.every((e) => selectedEmpIds.includes(e.id));
    const canSubmit = outgoingId && incomingId && outgoingId !== incomingId && !submitting;

    return (
        <div style={{ maxWidth: 800, margin: "0 auto", padding: "24px 16px" }}>

            {/* Alerts */}
            {success && (
                <Alert
                    type="success"
                    message={success}
                    showIcon
                    closable
                    onClose={() => setSuccess("")}
                    style={{ marginBottom: 16 }}
                />
            )}
            {error && (
                <Alert
                    type="error"
                    message={error}
                    showIcon
                    closable
                    onClose={() => setError("")}
                    style={{ marginBottom: 16 }}
                />
            )}

            {/* HOD Selection Row */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                {/* Outgoing HOD */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", display: "block", marginBottom: 6 }}>
                        Outgoing HOD <Tag color="red" style={{ marginLeft: 4 }}>OUT</Tag>
                    </label>
                    <Select
                        showSearch
                        placeholder="Search & select outgoing HOD"
                        style={{ width: "100%" }}
                        loading={loadingHods}
                        value={outgoingId}
                        onChange={handleOutgoingChange}
                        allowClear
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        options={hods
                            .filter((h) => h.id !== incomingId)
                            .map((h) => ({
                                value: h.id,
                                label: `${getLabel(h)} · ${h.department_name || ""}`,
                            }))
                        }
                    />
                    {outgoingEmp && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, fontSize: 12, color: "#dc2626" }}>
                            <strong>{getLabel(outgoingEmp)}</strong>
                            <div style={{ color: "#8fa3bc", marginTop: 2 }}>{outgoingEmp.emp_code} · {outgoingEmp.designation_name} · {outgoingEmp.department_name}</div>
                        </div>
                    )}
                </div>

                {/* Incoming HOD */}
                <div>
                    <label style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f", display: "block", marginBottom: 6 }}>
                        Incoming HOD <Tag color="green" style={{ marginLeft: 4 }}>IN</Tag>
                    </label>
                    <Select
                        showSearch
                        placeholder="Search & select incoming HOD"
                        style={{ width: "100%" }}
                        loading={loadingEmps}
                        value={incomingId}
                        onChange={(v) => { setIncomingId(v); setSuccess(""); setError(""); }}
                        allowClear
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase())
                        }
                        options={allEmployees
                            .filter((e) => e.id !== outgoingId)
                            .map((e) => ({
                                value: e.id,
                                label: `${getLabel(e)} · ${e.department_name || ""}`,
                            }))
                        }
                    />
                    {incomingEmp && (
                        <div style={{ marginTop: 8, padding: "8px 12px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, fontSize: 12, color: "#15803d" }}>
                            <strong>{getLabel(incomingEmp)}</strong>
                            <div style={{ color: "#8fa3bc", marginTop: 2 }}>{incomingEmp.emp_code} · {incomingEmp.designation_name} · {incomingEmp.department_name}</div>
                        </div>
                    )}
                </div>
            </div>

            {/* Reporting Employees */}
            {outgoingId && (
                <>
                    <Divider style={{ margin: "20px 0 16px" }} />
                    <div style={{ marginBottom: 10, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: "#1e3a5f", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Employees Reporting to Outgoing HOD
                        </span>
                        <Tag color={selectedEmpIds.length > 0 ? "blue" : "default"}>
                            {selectedEmpIds.length}/{reportingEmployees.length} selected
                        </Tag>
                    </div>

                    {loadingReporting ? (
                        <div style={{ color: "#8fa3bc", fontSize: 13, padding: "12px 0" }}>Loading employees…</div>
                    ) : reportingEmployees.length === 0 ? (
                        <div style={{ color: "#8fa3bc", fontSize: 13, padding: "12px 0" }}>No employees reporting to this HOD.</div>
                    ) : (
                        <div style={{ background: "#f8fafd", border: "1.5px solid #dde3ed", borderRadius: 10, padding: "12px 16px" }}>
                            {/* Select All */}
                            <div style={{ marginBottom: 10, paddingBottom: 10, borderBottom: "1px solid #dde3ed", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                <Checkbox
                                    checked={allSelected}
                                    indeterminate={selectedEmpIds.length > 0 && !allSelected}
                                    onChange={handleToggleAll}
                                >
                                    <span style={{ fontSize: 12, fontWeight: 600, color: "#5a82a6" }}>Select All</span>
                                </Checkbox>
                            </div>

                            {/* Employee List */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {reportingEmployees.map((emp) => {
                                    const isSelected = selectedEmpIds.includes(emp.id);
                                    return (
                                        <div
                                            key={emp.id}
                                            onClick={() => handleToggleEmp(emp.id)}
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 10,
                                                padding: "8px 10px",
                                                borderRadius: 8,
                                                cursor: "pointer",
                                                border: `1.5px solid ${isSelected ? "#93c5fd" : "transparent"}`,
                                                background: isSelected ? "#eff6ff" : "#ffffff",
                                                transition: "all 0.15s",
                                            }}
                                        >
                                            <Checkbox checked={isSelected} onChange={() => handleToggleEmp(emp.id)} onClick={(e) => e.stopPropagation()} />
                                            <div>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: "#1e3a5f" }}>
                                                    {getLabel(emp)}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#8fa3bc" }}>
                                                    {emp.emp_code} · {emp.designation_name} · {emp.department_name}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Transfer Preview */}
            {outgoingEmp && incomingEmp && (
                <>
                    <Divider style={{ margin: "20px 0 16px" }} />
                    <div style={{ background: "#f8fafd", border: "1.5px solid #dde3ed", borderRadius: 10, padding: "14px 16px", fontSize: 13 }}>
                        <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.08em", color: "#5a82a6", textTransform: "uppercase", marginBottom: 10 }}>
                            Transfer Preview
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <Tag color="red">{getLabel(outgoingEmp)} — {outgoingEmp.department_name}</Tag>
                            <span style={{ color: "#8fa3bc" }}>→</span>
                            <Tag color="green">{getLabel(incomingEmp)} — {incomingEmp.department_name}</Tag>
                            {selectedEmpIds.length > 0 && (
                                <>
                                    <span style={{ color: "#8fa3bc" }}>·</span>
                                    <Tag color="blue">{selectedEmpIds.length} employee{selectedEmpIds.length !== 1 ? "s" : ""} transferring</Tag>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Footer Buttons */}
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24, paddingTop: 20, borderTop: "1px solid #dde3ed" }}>
                <Button icon={<ReloadOutlined />} onClick={reset} disabled={submitting}>
                    Reset
                </Button>
                <Button
                    type="primary"
                    icon={<CheckOutlined />}
                    disabled={!canSubmit}
                    loading={submitting}
                    onClick={replaceHod}
                    style={{ backgroundColor: "#0e497b", borderColor: "#0e497b" }}
                >
                    Confirm Replacement
                </Button>
            </div>
        </div>
    );
}